import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Plus, X } from "lucide-react";
import { contact, services, solutionsMenu } from "@/data/site";
import { EASE } from "./motion";
import { Wordmark } from "./Header";

const itemVariants = {
  hidden: { opacity: 0, y: 26 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: EASE, delay: 0.12 + i * 0.07 },
  }),
};

export default function MobileMenu({ open, onClose, go }) {
  const [expanded, setExpanded] = useState(null);
  const navigate = useNavigate();

  const handleGo = (target) => {
    onClose();
    window.setTimeout(() => {
      if (target.startsWith("#")) go(target);
      else navigate(target);
    }, 150);
  };

  const groups = [
    { key: "services", label: "Services", children: services.map((s) => ({ label: s.title, to: `/services#${s.slug}` })), hash: "/services" },
    { key: "solutions", label: "Solutions", children: solutionsMenu.map((s) => ({ label: s.title, to: `/solutions#${s.slug}` })), hash: "/solutions" },
    { key: "products", label: "Products", hash: "/products" },
    { key: "about", label: "About", hash: "/about" },
    { key: "contact", label: "Contact", hash: "/contact" },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="mobile-menu"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[80] flex flex-col bg-navy text-white"
        >
          <div className="flex h-[72px] items-center justify-between px-6">
            <Wordmark light />
            <button
              type="button"
              data-testid="mobile-menu-close-button"
              aria-label="Close menu"
              onClick={onClose}
              className="grid h-10 w-10 place-items-center rounded-[10px] border border-white/15 text-white transition-colors duration-200 hover:border-white/40"
            >
              <X size={18} />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-6 pb-6 pt-4" aria-label="Mobile">
            {groups.map((g, i) => (
              <motion.div
                key={g.key}
                custom={i}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                className="border-b border-white/10"
              >
                <button
                  type="button"
                  data-testid={`mobile-nav-${g.key}`}
                  onClick={() =>
                    g.children
                      ? setExpanded(expanded === g.key ? null : g.key)
                      : handleGo(g.hash)
                  }
                  className="flex w-full items-center justify-between py-5 text-left"
                >
                  <span className="flex items-baseline gap-4">
                    <span className="font-mono text-[11px] tracking-[0.2em] text-slate-500">
                      0{i + 1}
                    </span>
                    <span className="text-[26px] font-bold tracking-tight">{g.label}</span>
                  </span>
                  {g.children && (
                    <Plus
                      size={18}
                      className={`text-slate-400 transition-transform duration-300 ${
                        expanded === g.key ? "rotate-45" : ""
                      }`}
                    />
                  )}
                </button>
                <AnimatePresence>
                  {g.children && expanded === g.key && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-col pb-5 pl-10">
                        {g.children.map((c) => (
                          <button
                            key={c.label}
                            type="button"
                            data-testid={`mobile-nav-${g.key}-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            onClick={() => handleGo(c.to)}
                            className="py-2.5 text-left text-[15px] text-slate-300 transition-colors duration-200 hover:text-white"
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </nav>

          <motion.div
            custom={5}
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            className="px-6 pb-10"
          >
            <button
              type="button"
              data-testid="mobile-start-project-button"
              onClick={() => handleGo("/contact")}
              className="group flex w-full items-center justify-center gap-2 rounded-[12px] bg-white py-4 text-[15px] font-semibold text-navy transition-colors duration-300 hover:bg-blue-600 hover:text-white"
            >
              Start a Project
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
            <p className="mt-6 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
              {contact.email} · {contact.phone}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
